import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { greeting, settings } from "../../portfolio";
import type { ThemeWithOptionalSetterProps } from "types";
import { CgSun } from "react-icons/cg";
import { HiMoon } from "react-icons/hi";

interface NavItem {
  to: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/home", label: "Home" },
  { to: "/education", label: "Education" },
  { to: "/experience", label: "Experience" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

const getHeaderClassName = ({
  isMobile,
  isMenuOpen,
  scrolled,
}: {
  isMobile: boolean;
  isMenuOpen: boolean;
  scrolled: boolean;
}): string => {
  if (isMobile) {
    if (isMenuOpen) {
      return "bg-bg-primary backdrop-blur-md border-b border-border shadow-lg";
    }

    return scrolled
      ? "bg-bg-primary/95 backdrop-blur-md border-b border-border"
      : "bg-transparent border-b border-transparent";
  }

  return scrolled
    ? "bg-bg-primary/80 backdrop-blur-xl border-b border-border"
    : "bg-transparent border-b border-transparent";
};

const Header: React.FC<ThemeWithOptionalSetterProps> = ({
  theme,
  setTheme,
}) => {
  const isLight = theme.name === "light";
  const link = settings.isSplash ? "/splash" : "/home";
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    globalThis.window === undefined
      ? false
      : globalThis.window.innerWidth < 768,
  );
  const location = useLocation();
  const headerClassName = getHeaderClassName({
    isMobile,
    isMenuOpen,
    scrolled,
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(globalThis.window.innerWidth < 768);
    };

    globalThis.window.addEventListener("resize", handleResize);
    handleResize();

    return () => globalThis.window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile || !isMenuOpen) return;

    const previousOverflow = globalThis.document.body.style.overflow;
    globalThis.document.body.style.overflow = "hidden";

    return () => {
      globalThis.document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, isMenuOpen]);

  function changeTheme() {
    const next = isLight ? "dark" : "light";
    setTheme?.(next);
    localStorage.setItem("theme", next);
  }

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${headerClassName}`}
    >
      <header className="relative z-50 flex items-center justify-between max-w-6xl px-4 py-4 mx-auto sm:px-6 lg:px-8">
        <NavLink
          to={link}
          onClick={() => setIsMenuOpen(false)}
          className="inline-flex items-center min-h-[44px] font-mono text-sm tracking-[0.08em] uppercase text-text-primary hover:text-accent transition-colors duration-200"
        >
          {greeting.logo_name}
        </NavLink>

        {/* Desktop nav */}
        <nav className="items-center hidden gap-8 md:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `relative font-mono text-xs tracking-[0.12em] uppercase transition-colors duration-200 link-underline ${
                  isActive
                    ? "text-accent"
                    : "text-text-muted hover:text-text-primary"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            onClick={changeTheme}
            className="flex items-center justify-center w-8 h-8 transition-colors duration-200 text-text-muted hover:text-accent"
            aria-label="Toggle theme"
          >
            {isLight ? <HiMoon size={16} /> : <CgSun size={16} />}
          </button>
        </nav>

        {/* Mobile menu toggle */}
        <button
          type="button"
          className="relative flex h-12 w-12 items-center justify-center rounded-xl border border-border/60 bg-bg-elevated/70 text-text-primary transition-colors duration-200 hover:border-accent/50 hover:text-accent md:hidden"
          onClick={() => setIsMenuOpen((current) => !current)}
          aria-label="Toggle mobile menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-nav-menu"
        >
          <span
            className={`absolute block h-px w-5 bg-current transition-all duration-200 ${
              isMenuOpen ? "rotate-45" : "-translate-y-[5px]"
            }`}
          />
          <span
            className={`absolute block h-px w-5 bg-current transition-all duration-200 ${
              isMenuOpen ? "opacity-0" : "opacity-100"
            }`}
          />
          <span
            className={`absolute block h-px w-5 bg-current transition-all duration-200 ${
              isMenuOpen ? "-rotate-45" : "translate-y-[5px]"
            }`}
          />
        </button>

        {/* Mobile nav */}
        <div
          className={`fixed inset-0 z-40 bg-black/45 backdrop-blur-[1px] md:hidden transition-opacity duration-300 ${
            isMenuOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />

        <div
          id="mobile-nav-menu"
          className={`absolute top-full left-0 right-0 z-50 bg-bg-primary border-b border-border shadow-xl md:hidden flex flex-col items-stretch gap-2 px-4 pb-6 pt-3 overflow-y-auto transition-all duration-300 ${
            isMenuOpen
              ? "max-h-[calc(100svh-76px)] opacity-100"
              : "max-h-0 opacity-0 pointer-events-none"
          }`}
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsMenuOpen(false)}
              className={({ isActive }) =>
                `flex w-full items-center min-h-[48px] rounded-xl px-4 py-3 font-mono text-sm tracking-[0.1em] uppercase transition-colors duration-200 ${
                  isActive
                    ? "bg-accent/12 text-accent"
                    : "bg-bg-elevated/55 text-text-muted hover:text-text-primary"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            onClick={changeTheme}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-border/70 px-4 py-3 min-h-[48px] text-text-muted transition-colors duration-200 hover:text-accent"
            aria-label="Toggle theme"
          >
            {isLight ? <HiMoon size={16} /> : <CgSun size={16} />}
            <span className="font-mono text-xs uppercase tracking-[0.1em]">
              {isLight ? "Dark mode" : "Light mode"}
            </span>
          </button>
        </div>
      </header>
    </div>
  );
};

export default Header;
